"use client";

import { useState, useEffect } from "react";
import { createStorefrontApiClient } from "@shopify/storefront-api-client";
import { ShoppingCart, ExternalLink, Minus } from "lucide-react";

import { toast } from "sonner";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { ButtonMaster } from "@/components/Atoms/Buttons";
import { MultiPurposeTitle } from "@/components/Atoms/Title";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import styles from "./index.module.scss";

interface ShopifyMiniCartProps {
  domain: string;
  storefrontAccessToken: string;
  label?: string;
  color?: any;
  cta?: string;
  type?: any;
  customColor?: any;
  config: any;
}

export default function ShopifyMiniCart({
  domain,
  storefrontAccessToken,
  label,
  color,
  cta,
  type,
  customColor,
  config,
}: ShopifyMiniCartProps) {
  const [cart, setCart] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cartId, setCartId] = useState<string | null>(null);
  const [cartOpen, setCartOpen] = useState(false);

  // Initialize Shopify client
  const getClient = () => {
    // Make sure domain is properly formatted
    let formattedDomain = domain;
    if (!formattedDomain.startsWith("http")) {
      formattedDomain = `https://${formattedDomain}`;
    }

    try {
      return createStorefrontApiClient({
        storeDomain: formattedDomain,
        apiVersion: "2024-10",
        publicAccessToken: storefrontAccessToken,
      });
    } catch (error) {
      console.error("Error initializing Shopify client:", error);
      toast.error("Failed to initialize Shopify client");
      return null;
    }
  };

  // Fetch cart data
  const fetchCart = async () => {
    setIsLoading(true);
    try {
      const storedCartId = localStorage.getItem("shopifyCartId");
      if (!storedCartId) {
        setIsLoading(false);
        return;
      }

      setCartId(storedCartId);
      const client = getClient();
      if (!client) {
        throw new Error("Failed to initialize Shopify client");
      }

      const cartQuery = `
        query GetCart($cartId: ID!) {
          cart(id: $cartId) {
            id
            checkoutUrl
            totalQuantity
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
            }
            lines(first: 10) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      product {
                        title
                        handle
                      }
                      image {
                        url
                        altText
                      }
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const { data, errors } = await client.request(cartQuery, {
        variables: { cartId: storedCartId },
      });

      if (errors) {
        console.error("GraphQL errors:", JSON.stringify(errors, null, 2));
        throw new Error(
          `Failed to fetch cart: ${errors.message || JSON.stringify(errors)}`
        );
      }

      if (data?.cart) {
        setCart(data.cart);
      }
    } catch (error: any) {
      console.error("Error fetching cart:", error);
      // If cart not found, don't show error toast
      if (!error.message?.includes("Cart not found")) {
        toast.error(error.message || "Failed to load cart information");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Remove item from cart
  const removeItem = async (lineId: string) => {
    try {
      if (!cartId) return;

      const client = getClient();
      if (!client) {
        throw new Error("Failed to initialize Shopify client");
      }

      const removeItemMutation = `
        mutation RemoveItem($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart {
              id
              totalQuantity
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const { data, errors } = await client.request(removeItemMutation, {
        variables: {
          cartId,
          lineIds: [lineId],
        },
      });

      if (errors || data?.cartLinesRemove?.userErrors?.length > 0) {
        throw new Error("Failed to remove item from cart");
      }

      // Refresh cart data
      fetchCart();
      toast.success("Item removed from cart");
    } catch (error) {
      console.error("Error removing item:", error);
      toast.error("Failed to remove item from cart");
    }
  };

  // Listen for cart updates
  useEffect(() => {
    fetchCart();

    // Set up event listener for cart updates
    const handleCartUpdate = () => {
      fetchCart();
      setCartOpen(true); // Open cart when items are added
    };

    window.addEventListener("shopify:cart:updated", handleCartUpdate);

    return () => {
      window.removeEventListener("shopify:cart:updated", handleCartUpdate);
    };
  }, [domain, storefrontAccessToken]);

  // Format price
  const formatPrice = (amount: string, currencyCode: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).format(Number.parseFloat(amount));
  };

  const cartItemCount = cart?.totalQuantity || 0;
  const cartSubtotal = cart?.cost?.subtotalAmount
    ? formatPrice(
        cart.cost.subtotalAmount.amount,
        cart.cost.subtotalAmount.currencyCode
      )
    : "$0.00";

  const style = {
    "--highlightColor": config?.highlightColor?.color?.hex,
    "--labelColor": color?.hex,
    "--bgColor": config?.backgroundColor?.color?.hex,
  } as React.CSSProperties;

  return (
    <>
      {cartItemCount > 0 && (
        <Sheet open={cartOpen} onOpenChange={setCartOpen}>
          <SheetTrigger asChild>
            <button
              aria-label="Open cart"
              style={style}
              className={`fixed top-[100px] lg:top-[150px] right-12 z-[1000] p-3 ${styles.button}`}
              onClick={() => {
                fetchCart();
                setCartOpen(true); // Explicitly set cart open state to true
              }}
            >
              <ShoppingCart color="white" className="h-5 w-5" />
              {cartItemCount > 0 && (
                <Badge className="absolute -top-2 -right-2 px-1 w-5 h-5 flex items-center justify-center bg-black text-white">
                  {cartItemCount}
                </Badge>
              )}
            </button>
          </SheetTrigger>
          <SheetContent
            style={style}
            className={`w-full sm:max-w-md flex flex-col ${styles.sheet}`}
          >
            <SheetHeader>
              <SheetTitle>
                <MultiPurposeTitle
                  data={"Your Cart"}
                  color={config?.titleColor?.color}
                  tag="p"
                  type="headline"
                />
              </SheetTitle>
              <SheetDescription>
                <MultiPurposeParagraph
                  data={
                    cartItemCount === 0
                      ? ""
                      : `${cartItemCount} items in your cart`
                  }
                  color={config?.textColor?.color}
                  type="image"
                />
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto py-4">
              {isLoading ? (
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <Skeleton className="h-16 w-16" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Skeleton className="h-16 w-16" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                </div>
              ) : cartItemCount === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-center">
                  <ShoppingCart className="h-12 w-12 text-muted-foreground mb-2" />

                  <MultiPurposeParagraph
                    data={"Your cart is empty"}
                    color={config?.textColor?.color}
                    type="image"
                  />
                  <SheetClose asChild>
                    <ButtonMaster
                      anim={"slideLeft"}
                      type={type}
                      color={cta}
                      cta={color}
                      customColor={customColor}
                    >
                      Continue Shopping
                    </ButtonMaster>
                  </SheetClose>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart?.lines?.edges.map(({ node }: any) => (
                    <Card key={node.id} className="overflow-hidden">
                      <CardContent className="p-3">
                        <div className="flex gap-3">
                          <div className="h-16 w-16  overflow-hidden flex-shrink-0">
                            {node.merchandise.image ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img
                                src={
                                  node.merchandise.image.url ||
                                  "/placeholder.svg"
                                }
                                alt={
                                  node.merchandise.image.altText ||
                                  node.merchandise.product.title
                                }
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full bg-muted flex items-center justify-center">
                                <ShoppingCart className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <MultiPurposeParagraph
                              weight={"bold"}
                              data={node.merchandise.product.title}
                              color={config?.textColor?.color}
                              type="label"
                            />
                            {node.merchandise.title !== "Default Title" && (
                              <MultiPurposeParagraph
                                data={node.merchandise.title}
                                color={config?.textColor?.color}
                                type="image"
                              />
                            )}
                            <div className="flex justify-between items-start  gap-2 flex-col">
                              <div>
                                {" "}
                                {/* Wrapper for price info */}
                                <div className="flex items-center gap-2">
                                  <MultiPurposeParagraph
                                    data={formatPrice(
                                      node.merchandise.price.amount,
                                      node.merchandise.price.currencyCode
                                    )}
                                    color={config?.highlightColor?.color}
                                    type="image"
                                  />
                                  <MultiPurposeParagraph
                                    data={`x ${node.quantity}`}
                                    color={config?.highlightColor?.color}
                                    type="image"
                                  />
                                </div>
                                <MultiPurposeParagraph
                                  data={`Total: ${formatPrice(
                                    String(
                                      Number.parseFloat(
                                        node.merchandise.price.amount
                                      ) * node.quantity
                                    ),
                                    node.merchandise.price.currencyCode
                                  )}`}
                                  color={config?.highlightColor?.color}
                                  type="image"
                                  className="text-xs mt-1" // Added styling for total
                                />
                              </div>
                              <div className="flex w-full justify-end">
                                <ButtonMaster
                                  anim={"slideLeft"}
                                  type={type}
                                  color={config?.highlightColor?.color}
                                  cta={config?.highlightColor}
                                  customColor={customColor}
                                  onClick={() => removeItem(node.id)}
                                >
                                  Remove
                                  <Minus className="ml-1 h-4 w-4" />
                                </ButtonMaster>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {cartItemCount > 0 && (
              <SheetFooter
                style={style}
                className={`border-t pt-4 ${styles.sheetFooter}`}
              >
                <div className="w-full space-y-4">
                  <div className="flex justify-between">
                    <MultiPurposeTitle
                      data={"Subtotal"}
                      color={config?.textColor?.color}
                      tag="p"
                      type="headline"
                    />
                    <MultiPurposeTitle
                      data={cartSubtotal}
                      color={config?.textColor?.color}
                      tag="p"
                      type="headline"
                    />
                  </div>

                  <MultiPurposeParagraph
                    data={"Shipping and taxes calculated at checkout"}
                    color={config?.textColor?.color}
                    type="image"
                  />
                  <div className=" gap-2 w-full grid ">
                    <ButtonMaster
                      anim={"slideLeft"}
                      type={type}
                      color={color}
                      cta={cta}
                      customColor={customColor}
                    >
                      <a
                        href={cart?.checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Checkout <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </ButtonMaster>
                    <SheetClose asChild>
                      <div className="w-full flex ">
                        <ButtonMaster
                          anim={"slideLeft"}
                          type={type}
                          color={color}
                          cta={cta}
                          customColor={customColor}
                        >
                          Continue Shopping
                        </ButtonMaster>
                      </div>
                    </SheetClose>
                  </div>
                </div>
              </SheetFooter>
            )}
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
